/* eslint-disable max-lines */
/* eslint-disable @typescript-eslint/no-empty-function */
import { createContext, RefObject, useCallback, useContext, useRef, useState } from 'react';
import { SearchPromotionItem } from '../components/SearchHeader/Suggestions/EmptySearchSuggestions/SearchPromotionResponse.type';
import { useSearchFilters } from '../components/SearchHeader/filters/useSearchFilters';
import { FilterChoice } from '../components/SearchHeader/types/FilterChoice';
import { renderLocationChoice } from '../components/SearchHeader/types/LocationChoice';
import { ChildrenOnly } from '../types/ChildrenOnly';

export const renderQuery = (filters: FilterChoice): string => {
  const { location, topic, text } = filters;

  const { treatment, specialist, bulkBillingGp } = topic || {};

  if (text) return text;

  let displayValue: string = '';

  const suggestionType = bulkBillingGp ? 'Bulk Billing GP' : treatment ?? specialist;

  if (suggestionType) {
    displayValue += `${suggestionType} in `;
  }

  if (location) {
    const locationText = renderLocationChoice(location) ?? '';
    displayValue += locationText;
  }

  return displayValue;
};

export interface SearchData {
  mobileSearchText: string;
  setMobileSearchText: Setter<string>;
  desktopSearchText: string;
  setDesktopSearchText: Setter<string>;
  inlineSearchText: string;
  setInlineSearchText: Setter<string>;
  focusMobileSearch: boolean;
  setFocusMobileSearch: Setter<boolean>;
  focusDesktopSearch: boolean;
  setFocusDesktopSearch: Setter<boolean>;
  focusInlineSearch: boolean;
  setFocusInlineSearch: Setter<boolean>;
  expandSearch: boolean;
  setExpandSearch: Setter<boolean>;
  searchSpecialty: SearchPromotionItem | null;
  setSearchSpecialty: Setter<SearchPromotionItem | null>;
  toggleMobileSearch: (val?: boolean) => void;
  toggleDesktopSearch: (searchText: string) => void;
  toggleInlineSearch: (searchText: string) => void;
  inlineSearchInputRef: RefObject<HTMLInputElement>;
  inlineSearchSuggestionsRef: RefObject<HTMLInputElement>;
  desktopSearchInputRef: RefObject<HTMLInputElement>;
  activeSpecialtyIndex: number;
  setActiveSpecialtyIndex: (val: number) => void;
}

export const defaultState: SearchData = {
  mobileSearchText: '',
  setMobileSearchText: (_value: string) => {},
  desktopSearchText: '',
  setDesktopSearchText: (_value: string) => {},
  inlineSearchText: '',
  setInlineSearchText: (_value: string) => {},
  focusMobileSearch: false,
  setFocusMobileSearch: (_showSearch: boolean) => {},
  focusDesktopSearch: false,
  setFocusDesktopSearch: (_showSearch: boolean) => {},
  focusInlineSearch: false,
  setFocusInlineSearch: (_showSearch: boolean) => {},
  expandSearch: false,
  setExpandSearch: (_expandSearch: boolean) => {},
  searchSpecialty: null,
  setSearchSpecialty: (_searchSpecialty: SearchPromotionItem | null) => {},
  toggleMobileSearch: (_val?: boolean) => {},
  toggleDesktopSearch: (_searchText: string) => {},
  toggleInlineSearch: (_searchText: string) => {},
  inlineSearchInputRef: { current: null },
  inlineSearchSuggestionsRef: { current: null },
  desktopSearchInputRef: { current: null },
  activeSpecialtyIndex: -1,
  setActiveSpecialtyIndex: (_val: number) => {},
};

export const SearchContext = createContext(defaultState);

export const SearchProvider = ({ children }: ChildrenOnly) => {
  const [filters] = useSearchFilters();

  // Provider specific state
  const [activeSearchText, setActiveSearchText] = useState('');

  // Returned through useSearch()
  const [mobileSearchText, setMobileSearchText] = useState(renderQuery(filters) ?? '');
  const [focusMobileSearch, setFocusMobileSearch] = useState<boolean>(false);
  const [expandSearch, setExpandSearch] = useState<boolean>(false);
  const [searchSpecialty, setSearchSpecialty] = useState<SearchPromotionItem | null>(null);
  const [desktopSearchText, setDesktopSearchText] = useState('');
  const [inlineSearchText, setInlineSearchTextInternal] = useState('');
  const [focusDesktopSearch, setFocusDesktopSearch] = useState<boolean>(false);
  const [focusInlineSearch, setFocusInlineSearch] = useState<boolean>(false);
  const inlineSearchInputRef = useRef<HTMLInputElement>(null);
  const inlineSearchSuggestionsRef = useRef<HTMLInputElement>(null);
  const desktopSearchInputRef = useRef<HTMLInputElement>(null);
  const [activeSpecialtyIndex, setActiveSpecialtyIndex] = useState(-1);

  const setInlineSearchText = useCallback((text: string) => {
    setInlineSearchTextInternal(text);
    inlineSearchInputRef.current?.focus();
  }, []);

  // Any component that adds a "Search" button where the intention is to perform a
  // mobile search should use this function. See SearchHeader.tsx or
  // BottomNavigationBar.tsx for examples.
  const toggleMobileSearch = useCallback(
    (val?: boolean) => {
      if (val !== undefined) {
        setExpandSearch(val);
        setFocusMobileSearch(val);
      } else {
        setExpandSearch(!focusMobileSearch);
        setFocusMobileSearch(!focusMobileSearch);
      }
    },
    [setExpandSearch, setFocusMobileSearch, focusMobileSearch],
  );

  // DESKTOP ONLY
  //
  // Any component that uses the search bar in the search header / home page
  // should use this function. See TopNavigationBar.tsx for an example
  const toggleDesktopSearch = useCallback(
    (searchText: string) => {
      if (activeSearchText === searchText) {
        setActiveSearchText('');
        setDesktopSearchText('');
        setFocusDesktopSearch(false);
      } else {
        setActiveSearchText(searchText);
        setDesktopSearchText(searchText + ' in ');
        setFocusDesktopSearch(true);
      }

      desktopSearchInputRef.current?.focus();

      // Unconditionally set the active specialty index to -1 so that the homepage
      // specialty buttons remove their active state. Their state is handled
      // manually in InlineSearch.tsx
      setActiveSpecialtyIndex(-1);
    },
    [activeSearchText, setDesktopSearchText],
  );

  // DESKTOP ONLY
  //
  // Any component that uses the search bar in the home page
  // should use this function. See TopNavigationBar.tsx for an example
  const toggleInlineSearch = useCallback(
    (searchText: string) => {
      if (activeSearchText === searchText) {
        setActiveSearchText('');
        setInlineSearchText('');
        setFocusInlineSearch(false);
      } else {
        setActiveSearchText(searchText);
        setInlineSearchText(searchText + ' in ');
        setFocusInlineSearch(true);
      }

      // Unconditionally set the active specialty index to -1 so that the homepage
      // specialty buttons remove their active state. Their state is handled
      // manually in InlineSearch.tsx
      setActiveSpecialtyIndex(-1);
    },
    [activeSearchText, setInlineSearchText],
  );

  return (
    <SearchContext.Provider
      value={{
        mobileSearchText,
        setMobileSearchText,
        focusMobileSearch,
        setFocusMobileSearch,
        expandSearch,
        setExpandSearch,
        searchSpecialty,
        setSearchSpecialty,
        toggleMobileSearch,
        toggleDesktopSearch,
        toggleInlineSearch,
        desktopSearchText,
        setDesktopSearchText,
        focusDesktopSearch,
        setFocusDesktopSearch,
        inlineSearchText,
        setInlineSearchText,
        focusInlineSearch,
        setFocusInlineSearch,
        inlineSearchInputRef,
        inlineSearchSuggestionsRef,
        desktopSearchInputRef,
        activeSpecialtyIndex,
        setActiveSpecialtyIndex,
      }}
    >
      {children}
    </SearchContext.Provider>
  );
};

export const useSearch = (): SearchData => useContext(SearchContext);
